import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Styled Components
const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 800;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const Tagline = styled.p`
  font-size: 1.2rem;
  color: #7f8c8d;
  font-style: italic;
  margin-top: 0.5rem;
`;

const TeachersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  margin-top: 2rem;
`;

const TeacherImageContainer = styled.div`
  width: 100%;
  height: 220px;
  overflow: hidden;
  position: relative;
  border-bottom: 1px solid #eee;
  background: #f5f5f5;
`;

const TeacherImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.5s ease;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
`;

const TeacherInfo = styled.div`
  padding: 1.8rem;
  
  h3 {
    margin: 0 0 0.8rem 0;
    color: #2c3e50;
    font-size: 1.3rem;
    font-weight: 700;
  }
`;

const SubjectBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #3498db, #2ecc71);
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(52,152,219,0.2);
`;

const ViewProfile = styled.div`
  margin: 1.5rem 0 0 0;
  color: #3498db;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  span {
    transition: transform 0.3s ease;
  }
`;

const ArrowIcon = styled.span`
  transition: transform 0.3s ease 0.1s;
  font-weight: 700;
`;

const TeacherCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  position: relative;
  z-index: 1;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
    
    &::after {
      opacity: 1;
    }
    
    ${TeacherImage} {
      transform: scale(1.1);
    }
    
    ${ViewProfile} span {
      transform: translateX(5px);
    }
    
    ${ArrowIcon} {
      transform: translateX(8px);
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(52,152,219,0.1), rgba(46,204,113,0.1));
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 16px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2.5rem;
  position: relative;
  box-shadow: 0 25px 50px rgba(0,0,0,0.3);
  animation: ${pulse} 8s infinite ease-in-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #7f8c8d;
  transition: all 0.3s ease;
  
  &:hover {
    color: #e74c3c;
    transform: rotate(90deg) scale(1.2);
  }
`;

const ModalImageContainer = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  margin-bottom: 1rem;
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 5px solid #f8f9fa;
  position: relative;
  z-index: 2;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
`;

const ModalImageGlow = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: linear-gradient(135deg, #3498db, #2ecc71);
  border-radius: 50%;
  z-index: 1;
  opacity: 0.3;
  filter: blur(10px);
  animation: ${float} 6s ease-in-out infinite;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  margin-bottom: 2.5rem;
  
  h2 {
    margin: 0 0 0.8rem 0;
    color: #2c3e50;
    font-size: 1.8rem;
    font-weight: 700;
  }
`;

const SubjectPill = styled(SubjectBadge)`
  background: linear-gradient(135deg, #e74c3c, #e67e22);
  font-size: 1rem;
  padding: 0.5rem 1.2rem;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
`;

const DetailItem = styled.div`
  h4 {
    margin: 0 0 0.8rem 0;
    color: #34495e;
    font-size: 1.1rem;
    font-weight: 600;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 50px;
      height: 3px;
      background: linear-gradient(90deg, #3498db, #2ecc71);
      border-radius: 3px;
    }
  }
  
  p {
    margin: 0;
    color: #7f8c8d;
    line-height: 1.7;
    font-size: 0.95rem;
  }
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const SkillTag = styled.span`
  background: #f8f9fa;
  color: #34495e;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #3498db, #2ecc71);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 10px rgba(52,152,219,0.2);
  }
`;

// Main Component
const TeachersPage = () => {
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const teachers = [
    {
      id: 1,
      name: "Er.Abhishek Sir",
      subject: "Software Developer",
      image: "/assets/my2.jpg", // Replace with your actual image path
      qualifications: "O Level With BCA And Data science ISO Certified",
      experience: "3 years",
      bio: "Specializes in making complex concepts simple. Awarded 'Best Web Teacher' in 2023 to 2025.",
      skills: ["JavaScript", "React Native", "Node.js", "MY SQL", "React JS","Web Development", "Software Development","Python","C","C++","C#","Bootstrap","UI/UX Designer","AI/ML","IOT"]
    },
    {
      id: 2,
      name: "Mr.Raj Sir",
      subject: "Python,IT ",
      image: "/assets/raj.jpg", // Replace with your actual image path
      qualifications: "IT Diploma (O Level, A Level) & BA",
      experience: "3 years",
      bio: "Always ready to work hard and learn new things",
      skills: ["Python", "C", "C++", "AI/ML", "Web Development"]
    },
    {
      id: 3,
      name: "Mr. Farhan Sir",
      subject: "English Literature",
      image: "/assets/farhaan.jpg",
      qualifications: "MCA ",
      experience: "4 years",
      bio: "Passionate about developing critical thinking through literature.",
      skills: ["Python","Machine Learning","HTML,CSS,JS","Personality Development"]
    },
    // {
    //   id: 4,
    //   name: "Ms. Ananya Patel",
    //   subject: "Computer Science",
    //   image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80",
    //   qualifications: "M.Tech in Computer Science",
    //   experience: "6 years",
    //   bio: "Tech enthusiast focused on practical coding skills. Lead developer of educational apps.",
    //   skills: ["Python", "Web Development", "AI Basics", "Machine Learning"]
    // }
  ];

  return (
    <PageContainer>
      <Header>
        <Title>Our Inspiring Faculty</Title>
        <Tagline>Shaping futures through innovative education</Tagline>
      </Header>

      <TeachersGrid>
        {teachers.map(teacher => (
          <TeacherCard key={teacher.id} onClick={() => setSelectedTeacher(teacher)}>
            <TeacherImageContainer>
              <TeacherImage 
                src={teacher.image} 
                alt={teacher.name} 
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "https://via.placeholder.com/300x220?text=Teacher+Image";
                }}
                style={{
                  objectPosition: teacher.id === 1 ? "center center" : "center top"
                }}
              />
              <ImageOverlay />
            </TeacherImageContainer>
            <TeacherInfo>
              <h3>{teacher.name}</h3>
              <SubjectBadge>{teacher.subject}</SubjectBadge>
              <ViewProfile>
                <span>View Profile</span>
                <ArrowIcon>→</ArrowIcon>
              </ViewProfile>
            </TeacherInfo>
          </TeacherCard>
        ))}
      </TeachersGrid>

      {selectedTeacher && (
        <ModalOverlay onClick={() => setSelectedTeacher(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedTeacher(null)}>
              ×
            </CloseButton>
            <ModalHeader>
              <ModalImageContainer>
                <ModalImage src={selectedTeacher.image} alt={selectedTeacher.name} />
                <ModalImageGlow />
              </ModalImageContainer>
              <div>
                <h2>{selectedTeacher.name}</h2>
                <SubjectPill>{selectedTeacher.subject}</SubjectPill>
              </div>
            </ModalHeader>
            
            <DetailsGrid>
              <DetailItem>
                <h4>Qualifications</h4>
                <p>{selectedTeacher.qualifications}</p>
              </DetailItem>
              <DetailItem>
                <h4>Experience</h4>
                <p>{selectedTeacher.experience}</p>
              </DetailItem>
              <DetailItem>
                <h4>About</h4>
                <p>{selectedTeacher.bio}</p>
              </DetailItem>
              <DetailItem>
                <h4>Specializations</h4>
                <SkillsContainer>
                  {selectedTeacher.skills.map((skill, index) => (
                    <SkillTag key={index}>
                      {skill}
                    </SkillTag>
                  ))}
                </SkillsContainer>
              </DetailItem>
            </DetailsGrid>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default TeachersPage;